import { getSupportedPaymentMethodsForCaja } from '@seek/payments-widget';
import { useTranslations } from '@vocab/react';
import { Stack, useToast } from 'braid-design-system';
import React, { useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { PRODUCT_EVENTS_PURCHASE } from 'src/app/analytics';
import { editPublishButtonClicked } from 'src/app/analytics/edit';
import { ROUTE_EDIT_SUCCESS } from 'src/app/constants/routes';
import getTrackingAdapter from 'src/app/dataAnalytics';
import { JOB_PUBLISH_PRESSED } from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { actionUpdateIsJobPosted } from 'src/app/state/modules/session/isJobPosted';
import { actionUpdateIsPaymentInProgress } from 'src/app/state/modules/session/isPaymentInProgress';
import { sendLinkEvent } from 'src/app/utils/analytics';
import { getProductCategories } from 'src/app/utils/analytics/utils/getProductCategories';
import { convertToCurrency } from 'src/app/utils/currency';
import { OrderSummary } from 'src/app/views/components/OrderSummary';
import { SystemBanner } from 'src/app/views/components/SystemBanner';
import { useDraft } from 'src/app/views/hooks/useDraft';
import { useErrorType } from 'src/app/views/hooks/useErrorType';
import { useLocalisedRoutes } from 'src/app/views/hooks/useLocalisedRoutes';
import { BasicPageLayout } from 'src/app/views/layouts/BasicPageLayout';
import { PageSection } from 'src/app/views/sections/PageSection';
import { BackButton } from 'src/app/views/shared/BackButton';
import { BenefitList } from 'src/app/views/shared/BenefitList';
import type AppState from 'src/types/appState';

import translations from './.vocab';
import { PaymentsSection } from './sections/PaymentsSection';

export const EditPayAndPostPage = () => {
  const { t } = useTranslations(translations);
  const {
    contractBalance,
    productTotalPrice,
    jobOrderId,
    hidePrice,
    seekAdvertiserId,
    tealium,
  } = useSelector(
    (state: AppState) => ({
      contractBalance: state.session.contractBalance,
      productTotalPrice: state.tealium.productTotalPrice,
      jobOrderId: state.session.jobOrderId,
      hidePrice: state.session.hirerAccount.hidePrice,
      seekAdvertiserId: state.session.seekAdvertiserId,
      tealium: state.tealium,
    }),
    shallowEqual,
  );

  const history = useHistory();
  const dispatch = useDispatch();
  const { saveDraft } = useDraft();
  const showToast = useToast();
  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

  const { goToRoute } = useLocalisedRoutes();

  const [hidePaymentDetails, setHidePaymentDetails] = useState(false);

  const handleHidePaymentDetails = () => {
    setHidePaymentDetails(true);
  };

  const handledCancelPayment = () => {
    dispatch(actionUpdateIsPaymentInProgress(false));
  };

  const costDifference =
    Number(convertToCurrency(Number(contractBalance))) -
    Number(productTotalPrice);

  const isPaymentDetailsHidden =
    hidePaymentDetails ||
    Boolean(typeof contractBalance === 'number' && costDifference <= 0);

  const { errorType, setError, resetError } = useErrorType();

  const onButtonClicked = async () => {
    const countryId = tealium.country ?? 'AU';
    const paymentMethods = getSupportedPaymentMethodsForCaja(
      countryId.toUpperCase(),
    );
    if (isPageAnalyticsEnabled) {
      const eventData = {
        eventName: JOB_PUBLISH_PRESSED,
        adProductSetId: tealium.productSetId,
        adStatus: 'open',
        brandHasCoverImage: tealium.hasCoverImage,
        brandHasLogo: tealium.hasLogo,
        jobAdTypeSelected: [tealium.jobAdTypeSelected],
        jobIsLinkOut: Boolean(tealium.isLinkOutApplyViaSeek),
        paymentMethodShown: paymentMethods,
        productCategories: getProductCategories(tealium.adBudgetPurchased),
        productEvents: [PRODUCT_EVENTS_PURCHASE],
        productSetProductIds: tealium.productSetProductIds,
        productTotalPrice,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    } else {
      sendLinkEvent(tealium, editPublishButtonClicked);
    }

    resetError();

    if (!jobOrderId) {
      throw new Error('missing orderId');
    }
  };

  const onSuccess = async () => {
    await saveDraft(true);
    dispatch(actionUpdateIsJobPosted(true));

    goToRoute(ROUTE_EDIT_SUCCESS);

    showToast({
      message: t('edit-pay-post-page-publish-message'),
      tone: 'positive',
    });
  };

  const onError = (error: Error) => {
    setError(error);
  };

  return (
    <BasicPageLayout
      title={t('edit-pay-post-page-title')}
      errorType={errorType}
    >
      <Stack space="large">
        <BackButton
          onClick={() => {
            history.goBack();
          }}
        >
          {t('edit-pay-post-page-back-link')}
        </BackButton>

        {hidePrice && (
          <PageSection
            heading={t('edit-pay-post-page-benefit-section-heading')}
          >
            <BenefitList
              items={[
                t('edit-pay-post-page-benefit-item-1'),
                t('edit-pay-post-page-benefit-item-2'),
              ]}
            />
          </PageSection>
        )}

        <Stack space="medium">
          <OrderSummary
            hidePaymentDetails={isPaymentDetailsHidden}
            handleHidePaymentDetails={handleHidePaymentDetails}
            stackSpace={'medium'}
          />
          <SystemBanner context={'job_posting_pay_and_post'} style="alert" />
          {Boolean(seekAdvertiserId) && (
            <PaymentsSection
              onPaymentSuccess={onSuccess}
              onPaymentFailed={onError}
              handledCancelPayment={handledCancelPayment}
              onPostAdButtonClicked={onButtonClicked}
              onPostAdButtonClickFailed={onError}
            />
          )}
        </Stack>
      </Stack>
    </BasicPageLayout>
  );
};
